import React, {Component} from 'react'
import PropTypes from 'prop-types'
import ScrollableAnchor from 'react-scrollable-anchor'
import cn from 'classnames'
import Swiper from 'react-id-swiper'
import withStyles from '@material-ui/core/styles/withStyles'
import Grid from '@material-ui/core/Grid'
import Hidden from '@material-ui/core/Hidden'
import ButtonBase from '@material-ui/core/ButtonBase'

import {ReactComponent as CheckIcon} from '../assets/images/icons/check.svg'
import ScrollAnimation from './ScrollAnimation'
import {renderBullet} from '../utils/helpers'
import PACKS from '../fixtures/packs'
import './Packs.scss'

const StyledButtonBase = withStyles({
  root: {
    borderRadius: 20,
    height: 40,
    paddingLeft: 16,
    paddingRight: 16,
    justifyContent: 'flex-start',
    backgroundColor: 'white',
    transition: 'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1)',
    '&:not(:last-child)': {
      marginBottom: 10,
    },
  },
})(ButtonBase)

const renderPackContent = pack => {
  return (
    <div className="Packs-PackContent">
      <div>
        <div className="Packs-PackIconBlock">
          <pack.Icon className="Packs-PackIcon" />
        </div>
        <h3 className="Packs-PackTitle">{pack.title}</h3>
        <p className="Packs-PackUnique">{pack.unique}</p>
        <p className="Packs-PackDescription">{pack.description}</p>
      </div>
      <div>
        <p className="Packs-PackPeriod">{pack.period}</p>
        <p className="Packs-PackPrice">
          <span className="Extras-ItemCurrency">$</span>
          {pack.price} nzd
        </p>
        <p className="Packs-PackDiscount">50% project kick off, 50% on delivery</p>
      </div>
    </div>
  )
}

const getSlideIndex = selectedPack => (selectedPack === 'Mini' ? 0 : selectedPack === 'Smart' ? 1 : 2)

export default class Packs extends Component {
  swiper = null

  static propTypes = {
    width: PropTypes.string.isRequired,
    sections: PropTypes.array.isRequired,
    sectionsState: PropTypes.object.isRequired,
    selectedPack: PropTypes.string.isRequired,

    onSectionSet: PropTypes.func.isRequired,
  }

  UNSAFE_componentWillReceiveProps(nextProps, nextContext) {
    const {width, selectedPack} = this.props
    if (width === 'xs' && selectedPack !== nextProps.selectedPack) {
      this.swiper.slideTo(getSlideIndex(nextProps.selectedPack))
    }
  }

  getSwiper = swiper => {
    this.swiper = swiper
  }

  renderSections(sections) {
    const {sectionsState, onSectionSet} = this.props
    return sections.map(section => (
      <StyledButtonBase
        key={section.id}
        className={cn('Packs-Section', {
          'Packs-Section_active': sectionsState[section.name],
        })}
        focusRipple
        component="li"
        onClick={onSectionSet(section.name)}
      >
        <div className="Packs-SectionCheckbox Checkbox">
          {sectionsState[section.name] && <CheckIcon className="CheckIcon FadeIn" />}
        </div>
        <p>
          {section.label}
          {section.extraLabel && <span className="Packs-SectionExtraLabel"> {section.extraLabel}</span>}
        </p>
      </StyledButtonBase>
    ))
  }

  get renderedPacks() {
    const {width, selectedPack} = this.props
    if (width === 'xs') {
      const settings = {
        grabCursor: true,
        preventClicksPropagation: false,
        centeredSlides: true,
        initialSlide: getSlideIndex(selectedPack),
        speed: 1000,
        pagination: {
          renderBullet,
          el: '.Bullets',
          clickable: true,
        },
        breakpoints: {
          340: {
            spaceBetween: -80,
          },
          370: {
            spaceBetween: -100,
          },
          400: {
            spaceBetween: -130,
          },
          430: {
            spaceBetween: -160,
          },
          460: {
            spaceBetween: -190,
          },
          490: {
            spaceBetween: -220,
          },
          550: {
            spaceBetween: -250,
          },
          600: {
            spaceBetween: -310,
          },
        },
      }
      return (
        <Swiper {...settings} getSwiper={this.getSwiper}>
          {PACKS.map(pack => (
            <div key={pack.id} className={cn('Packs-Pack', {'Packs-Pack_active': selectedPack === pack.title})}>
              {renderPackContent(pack)}
            </div>
          ))}
        </Swiper>
      )
    }
    return (
      <ul className="Packs-List">
        {PACKS.map((pack, index) => (
          <ScrollAnimation
            key={pack.id}
            tag="li"
            className={cn('Packs-Pack', {'Packs-Pack_active': selectedPack === pack.title})}
            effect={index === 0 ? 'fadeInLeft' : index === 1 ? 'fadeInBottomSmall' : 'fadeInRight'}
          >
            {renderPackContent(pack)}
          </ScrollAnimation>
        ))}
      </ul>
    )
  }

  render() {
    const {width, sections} = this.props
    const isDesktop = width !== 'xs' && width !== 'sm'
    return (
      <ScrollableAnchor id="packs">
        <section className="Packs Container">
          <Grid container spacing={2}>
            <Hidden mdDown>
              <Grid item xs={1} />
            </Hidden>
            <Grid item xs={12} lg={10}>
              <ScrollAnimation tag="h2" className="Packs-Title Title" effect="fadeInTop">
                Our packs
              </ScrollAnimation>
              <ScrollAnimation tag="p" className="Packs-Text" effect="fadeInBottom">
                We have designed our packs specifically for small business needs. To help you to choose which pack suits
                you best, please select what sections you would like to have on your website.
              </ScrollAnimation>
              <div className="Packs-Block">
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <ScrollAnimation tag="ul" className="Packs-Sections" effect="fadeInLeft">
                      {this.renderSections(isDesktop ? sections.slice(0, 5) : sections.slice(0, 8))}
                    </ScrollAnimation>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <ScrollAnimation
                      tag="ul"
                      className="Packs-Sections"
                      effect={isDesktop ? 'fadeInBottomSmall' : 'fadeInRight'}
                    >
                      {this.renderSections(isDesktop ? sections.slice(5, 10) : sections.slice(8))}
                    </ScrollAnimation>
                  </Grid>
                  <Hidden smDown>
                    <Grid item sm={12} md={4}>
                      <ScrollAnimation tag="ul" className="Packs-Sections" effect="fadeInRight">
                        {this.renderSections(sections.slice(10))}
                      </ScrollAnimation>
                    </Grid>
                  </Hidden>
                </Grid>
              </div>
              <div className="Packs-Spacer" />
              {this.renderedPacks}
            </Grid>
            <Hidden mdDown>
              <Grid item xs={1} />
            </Hidden>
          </Grid>
        </section>
      </ScrollableAnchor>
    )
  }
}
